import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useRiskPoolOrgContext } from '../../RiskPoolOrgContext';
import { usePolicyContext } from '../PolicyContext';
import usePolicyMetrics from '../_data_sources/usePolicyMetrics';
import { PolicyMetricsResponse, MetricStatus } from '../_types';
import { ChartInfo } from '_lex/_atoms/ChartInfo';

const getChartInfoColor = (status: MetricStatus): 'success' | 'warning' | 'error' | 'neutral' => {
  switch (status) {
    case 'GOOD':
      return 'neutral';
    case 'CONCERNING':
      return 'warning';
    case 'CRITICAL':
      return 'error';
    default:
      return 'neutral';
  }
};

const getCardsData = (data: PolicyMetricsResponse) => {
  const metrics = [
    {
      key: 'acknowledgments',
      // Percent values are already whole numbers (e.g., 51 for 51%), so display as "%"
      metric: `${data.acknowledgments_completed.percent}%`,
      description: `${data.acknowledgments_completed.count} of ${data.acknowledgments_completed.totalCount}`,
      status: data.acknowledgments_completed.status,
    },
    {
      key: 'dtbsTaken',
      metric: `${data.dtbs_taken.percent}%`,
      description: `${data.dtbs_taken.count} of ${data.dtbs_taken.totalCount}`,
      status: data.dtbs_taken.status,
    },
    {
      key: 'lexipolUpdates',
      metric: `${data.lexipol_updates.percent}%`,
      description: `${data.lexipol_updates.count} of ${data.lexipol_updates.totalCount}`,
      status: data.lexipol_updates.status,
    },
    {
      key: 'policiesPublished',
      metric: data.policies_published.total,
      metricFormat: 'number' as const,
      description: `${data.policies_published.percentChange > 0 ? '+' : ''}${data.policies_published.percentChange}%`,
      status: data.policies_published.status,
    },
    {
      key: 'dtbsIssued',
      metric: `${data.dtbs_issued.percent}%`,
      description: `${data.dtbs_issued.agencyIssuedCount} of ${data.dtbs_issued.lexipolIssuedCount}`,
      status: data.dtbs_issued.status,
    },
  ];

  return metrics.map((metric) => {
    const prefixKey = `dashboard.riskPool.riskPoolOrg.metrics.${metric.key}`;
    return {
      title: t(`${prefixKey}.title`),
      metric: metric.metric,
      metricFormat: metric.metricFormat,
      description: metric.description,
      color: getChartInfoColor(metric.status),
      tooltip: t(`${prefixKey}.tooltip`),
    };
  });
};

export function MetricsCards() {
  const { orgId, queryParams } = useRiskPoolOrgContext();
  const { manualId } = usePolicyContext();
  const { data: metrics, isLoading } = usePolicyMetrics(orgId, {
    ...queryParams,
    manualId: manualId !== 'all' ? manualId : undefined,
  });

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!metrics) {
    return null;
  }

  const cardsData = getCardsData(metrics);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 4,
      }}
    >
      {cardsData.map((card, index) => (
        <ChartInfo
          key={`metric-card-${index}`}
          title={card.title}
          metric={card.metric}
          metricFormat={card.metricFormat}
          description={card.description}
          color={card.color}
          tooltip={card.tooltip}
        />
      ))}
    </Box>
  );
}

export default MetricsCards;
